import { NavLink } from 'react-router-dom'

// ─── Mobile bottom navigation bar ─────────────────────────────────────────────
// Shown only on mobile (<md). Provides one-thumb access to the 5 primary routes.
// Each tap target is ≥44px tall to meet WCAG 2.5.5 and Apple HIG guidelines.

interface MobileNavItem {
  label: string
  to: string
  icon: string
}

const MOBILE_NAV_ITEMS: MobileNavItem[] = [
  { label: 'Home', to: '/', icon: '⊞' },
  { label: 'Live', to: '/live', icon: '▶' },
  { label: 'Chat', to: '/chat', icon: '💬' },
  { label: 'Alerts', to: '/alerts', icon: '🔔' },
  { label: 'Analytics', to: '/analytics', icon: '📊' },
]

interface MobileNavProps {
  onHamburgerClick: () => void
}

export function MobileNav({ onHamburgerClick }: MobileNavProps) {
  return (
    <nav
      aria-label="Mobile navigation"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-stretch"
      style={{
        backgroundColor: 'var(--bg-primary)',
        borderTop: '1px solid var(--border-primary)',
        // Safe area inset for iOS notched devices
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {MOBILE_NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          className={({ isActive }) =>
            [
              'flex flex-col items-center justify-center flex-1 gap-1 py-2',
              'min-h-[56px] text-[10px] font-medium transition-colors select-none',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500',
              isActive ? 'text-purple-400' : 'text-gray-500',
            ].join(' ')
          }
          style={({ isActive }) =>
            isActive ? { color: 'var(--accent-light)' } : {}
          }
        >
          <span className="text-xl leading-none" aria-hidden="true">
            {item.icon}
          </span>
          <span>{item.label}</span>
        </NavLink>
      ))}

      {/* More / hamburger — opens full sidebar drawer */}
      <button
        type="button"
        onClick={onHamburgerClick}
        aria-label="Open full navigation menu"
        className="flex flex-col items-center justify-center flex-1 gap-1 py-2 min-h-[56px] text-[10px] font-medium text-gray-500 hover:text-gray-200 transition-colors select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
      >
        <span className="text-xl leading-none" aria-hidden="true">
          ☰
        </span>
        <span>More</span>
      </button>
    </nav>
  )
}
